import React from 'react';

import Alert from '../ui/Alert';

export interface SubmissionErrorsProps {
  errors: Record<string, unknown>;
  // Inline help uses the input's label and the FormGroup's error styling.
  inline?: boolean;
  // When provided, each alert becomes dismissible; dismissing any clears all.
  onDismiss?: () => void;
}

// Reduce a raw error value to the messages it carries: a bare string, or
// an array of strings (DRF field errors / non_field_errors). Anything else
// (e.g. netwas's `stats` object) carries no message and is dropped so it
// never renders as "[object Object]".
function toMessages(value: unknown): string[] {
  if (typeof value === 'string') {
    return value.trim() !== '' ? [value] : [];
  }
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is string => typeof item === 'string' && item.trim() !== '',
    );
  }
  return [];
}

// Keys that are messages in their own right (DRF view-level + our synthetic
// transport errors), not the name of a form field.
const SUBMISSION_LEVEL_KEYS = new Set([
  'detail',
  'non_field_errors',
  'statusError',
  'fetchError',
]);

// Preserve the analysis acronyms and notation used by the form labels.
const FIELD_LABELS: Record<string, string> = {
  gwas_file: 'GWAS file',
  p_value: 'p-value',
  mhc: 'MHC region',
};

// The GWAS row list's closing count and hint are not errors: no icon.
const NOTE_PATTERN = /^(… and \d+ more|If you have -log10)/;

// Field errors identify their input; submission messages already stand alone.
function labelFor(key: string): string {
  if (SUBMISSION_LEVEL_KEYS.has(key)) {
    return '';
  }
  const label = FIELD_LABELS[key];
  if (label) {
    return `${label}: `;
  }
  const words = key.replace(/_/g, ' ');
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}: `;
}

/**
 * Renders a normalized job-submission error object (from core/jobSubmission's
 * submitJobForm) as danger alerts or inline field help. Handles server field
 * errors, DRF `detail` (401/403/429), and the synthetic
 * `statusError`/`fetchError`. Non-message values are skipped; an empty object
 * renders nothing. Shared by the NetWAS and DeepSEA forms.
 */
const SubmissionErrors: React.FC<SubmissionErrorsProps> = ({
  errors,
  inline = false,
  onDismiss,
}) => {
  const groups = Object.keys(errors)
    .map(key => ({ key, messages: toMessages(errors[key]) }))
    .filter(group => group.messages.length > 0);

  if (groups.length === 0) {
    return null;
  }

  return (
    <>
      {groups.map(({ key, messages }) => {
        if (inline) {
          return (
            // role="alert" announces the error when it appears, as the
            // danger alerts below do.
            <div key={key} className="help-block" role="alert">
              {messages.map((message, index) =>
                NOTE_PATTERN.test(message) ? (
                  // Lined up with the row text.
                  <div key={`${key}-${index}`} style={{ paddingLeft: '1.1em' }}>
                    {message}
                  </div>
                ) : (
                  // Preserve duplicate messages in this static server-provided list.
                  <div key={`${key}-${index}`}>
                    <i
                      className="fa fa-exclamation-circle"
                      aria-hidden="true"
                    />{' '}
                    {message}
                  </div>
                ),
              )}
            </div>
          );
        }
        const label = labelFor(key);
        return (
          <Alert
            key={key}
            bsStyle="danger"
            onDismiss={onDismiss}
            style={{ marginTop: 15 }}
          >
            {messages.length === 1 ? (
              <span>
                <i className="fa fa-exclamation-circle" aria-hidden="true" />
                {` ${label}${messages[0]}`}
              </span>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {messages.map((message, index) => (
                  // Static, never-reordered list; the index is a stable key
                  // and stays unique even when two messages are identical
                  // (message text alone would collide).
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={`${key}-${index}`}>{`${label}${message}`}</li>
                ))}
              </ul>
            )}
          </Alert>
        );
      })}
    </>
  );
};

export default SubmissionErrors;
